import firebase from './firebase';
import { getCitiesFromMarkets, getRolesFromMarkets } from './index';

/**
 * Create a new career development opportunity post
 * @param {Object} data - The career development opportunity data
 * @param {Object} user - The user creating the post
 */
export const createCareerOppPost = async (data, user) => {
  try {
    await firebase
      .firestore()
      .collection('users')
      .doc(firebase.auth().currentUser.uid)
      .collection('careerDevPosts')
      .add({
        created: new Date(),
        ...data,
        company: data.company || user.company,
      });
  } catch (e) {
    window.alert(e);
  }
};

/**
 * Edit an existing career development opportunity post
 * @param {Object} data - The updated career opportunity data
 * @param {Object} user - The user editing the post
 */
export const editCareerOppPost = async (data, user) => {
  try {
    await firebase
      .firestore()
      .collection('users')
      .doc(firebase.auth().currentUser.uid)
      .collection('careerDevPosts')
      .doc(data.id)
      .set({
        ...data,
        company: user.company || null,
      });
  } catch (e) {
    window.alert(e);
  }
};

/**
 * Delete a career development opportunity post
 * @param {string} id - The ID of the post to delete
 */
export const deleteCareerOppPost = async (id) => {
  try {
    await firebase
      .firestore()
      .collection('users')
      .doc(firebase.auth().currentUser.uid)
      .collection('careerDevPosts')
      .doc(id)
      .delete();
  } catch (e) {
    window.alert(e);
  }
};

/**
 * Get all career development opportunity posts for the current user
 * @returns {Promise<Array>} - Array of career opportunity posts
 */
export const getUserCareerOppPosts = async () => {
  try {
    const posts = await firebase
      .firestore()
      .collection('users')
      .doc(firebase.auth().currentUser.uid)
      .collection('careerDevPosts')
      .get();
    return posts.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
  } catch (e) {
    window.alert(e);
    return [];
  }
};

/**
 * Get all active career development opportunity posts across all users
 * @returns {Promise<Array>} - Array of active career opportunity posts
 */
export const getActiveCareerOppPosts = async () => {
  try {
    const posts = await firebase
      .firestore()
      .collectionGroup('careerDevPosts')
      .get();
    return posts.docs
      .map((doc) => ({ ...doc.data(), id: doc.id }))
      .filter(
        (post) =>
          post.endDate &&
          new Date(post.endDate.toDate()) > new Date(),
      );
  } catch (e) {
    window.alert(e);
    return [];
  }
};

/**
 * Decorate career opportunity post with related entity data
 * @param {Object} post - The career opportunity post
 * @param {Array} cities - Cities list
 * @param {Array} roles - Roles list
 * @returns {Object} - Decorated post with resolved references
 */
export const decorateCareerOppPost = (post, cities, roles) => {
  return {
    ...post,
    city: cities.find((c) => c.id === post.cityId)?.label,
    role: post.roleId === 'any' 
      ? 'Open to any Role'
      : roles.find((c) => c.id === post.roleId)?.label,
  };
};

/**
 * Get decorated career opportunity posts with related entity data
 * @param {Object} markets - Markets data
 * @param {Array} posts - Raw career opportunity posts
 * @returns {Promise<Array>} - Decorated career opportunity posts
 */
export const getDecoratedCareerOppPosts = async (markets, posts) => {
  const cities = getCitiesFromMarkets(markets);
  const roles = getRolesFromMarkets(markets);
  return posts.map((post) => {
    return decorateCareerOppPost(post, cities, roles);
  });
};

